import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { CircularProgress, IconButton, Modal, Typography } from '@mui/material';
import { Close, Keyboard, Mouse, Pinch, TouchApp, ZoomInMap } from '@mui/icons-material';
import styled from 'styled-components';
import { getPublicEnterpriseQuery } from '../../common/services/publicEnterprise';
import { getListImageStorage } from '../../common/services/Image';
import ImagePlant from '../../common/components/ImagePlant/ImagePlant';
import { Spacing } from '../../common/components/Spacing/Spacing';
import { COLORS } from '../../common/constants/COLORS';
import { PLANT_STATUS } from '../../common/constants/PLANT_STATUS';
import Enterprise from '../../pagesUser/Enterprise/Enterprise';
import { ButtonStyled } from '../../common/components/ButtonStyled/ButtonStyled';
import controloteLogo from '../../static/img/logo192.png';
import twoFingerMove from '../../static/img/two-finger-move.svg';

const PUBLIC_BACKGROUND = '#24272b';
const PLANT_TOP_OFFSET = 20;

const TwoFingerMoveIcon = styled.img.attrs({ src: twoFingerMove, alt: '' })`
  && {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }
`;

const HelpContent = styled.div`
  color: #f4f6f8;

  p {
    margin: 8px 0;
  }
`;

const HelpDialog = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(440px, calc(100% - 32px));
  box-sizing: border-box;
  padding: 22px 24px 24px;
  color: ${COLORS.WHITE};
  background: ${PUBLIC_BACKGROUND};
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.48);
  transform: translate(-50%, -50%);
  outline: none;
`;

const HelpDialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const LotDialog = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(420px, calc(100% - 32px));
  box-sizing: border-box;
  padding: 22px 24px 24px;
  color: ${COLORS.WHITE};
  background: ${PUBLIC_BACKGROUND};
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.48);
  transform: translate(-50%, -50%);
  outline: none;
`;

const LotDialogHeader = styled(HelpDialogHeader)`
  margin-bottom: 20px;
`;

const LotDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LotInfo = styled.div`
  padding: 13px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
`;

const LotInfoLabel = styled.div`
  margin-bottom: 4px;
  color: #a9b7cc;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const LotInfoValue = styled.div`
  color: ${COLORS.WHITE};
  font-size: 1rem;
  line-height: 1.35;
`;

const LotStatus = styled(LotInfoValue)`
  color: ${({ color }) => color};
  font-weight: 700;
`;

const HelpSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 600px) {
    gap: 6px;
  }
`;

const DesktopHelpSection = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 600px) {
    display: none;
  }
`;

const HelpStep = styled.div`
  display: flex;
  min-height: 104px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 22px 6px 10px;
  box-sizing: border-box;
  text-align: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;

  .MuiTypography-root {
    display: flex;
    min-height: 2.4em;
    align-items: center;
    justify-content: center;
  }

  svg,
  img {
    color: ${COLORS.GREEN_LIGHT};
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    object-fit: contain;
  }

  @media (max-width: 600px) {
    min-height: 94px;
    padding: 16px 4px 8px;

    svg,
    img {
      width: 28px;
      height: 28px;
      flex-basis: 28px;
    }
  }
`;

const Dashboard = styled.div`
  flex: 0 0 auto;
  width: 100%;
  padding: 10px 12px;
  box-sizing: border-box;
  color: ${COLORS.WHITE};
  background: ${PUBLIC_BACKGROUND};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0;
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const Brand = styled.div`
  display: flex;
  flex: 0 0 96px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;

  @media (max-width: 600px) {
    flex-basis: 34px;
  }
`;

const BrandLogo = styled.img`
  display: block;
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const BrandName = styled.span`
  color: ${COLORS.WHITE};
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 600px) {
    display: none;
  }
`;

const DashboardItems = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  flex: 1;

  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;
  }
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  flex-direction: column;
  min-height: 48px;
  padding: 8px 12px;
  color: ${COLORS.WHITE};
  background: #30343a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;

  @media (max-width: 600px) {
    grid-column: span 2;
    flex-direction: row;
    min-height: 28px;
    padding: 4px 6px;
    gap: 4px;
    border-radius: 6px;

    &:nth-child(4),
    &:nth-child(5) {
      grid-column: span 3;
    }
  }
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  flex: 0 0 10px;
  background: ${({ color }) => color};
  border-radius: 50%;

  @media (max-width: 600px) {
    width: 7px;
    height: 7px;
    flex-basis: 7px;
  }
`;

const DesktopMetric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  @media (max-width: 600px) {
    display: none;
  }
`;

const MobileMetric = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
`;

const STATUS_ITEMS = [
  { status: 'TOTAL', label: 'Total', compactLabel: 'Total', color: '#e5e7eb', isTotal: true },
  { status: PLANT_STATUS.AVAILABLE, label: 'Disponíveis', color: COLORS.GREEN },
  { status: PLANT_STATUS.RESERVED, label: 'Reservados', color: COLORS.YELLOW },
  { status: PLANT_STATUS.SOLD, label: 'Vendidos', color: '#ff4545' },
  { status: PLANT_STATUS.IN_PROPOSAL, label: 'Em proposta', compactLabel: 'Proposta', color: COLORS.BLUE },
];

const PlantDashboard = ({ forms, dashboardRef }) => {
  const counts = forms?.reduce((result, item) => {
    result[item?.status] = (result[item?.status] || 0) + 1;
    return result;
  }, {});
  const total = forms?.length || 0;
  return (
    <Dashboard ref={dashboardRef}>
      <DashboardHeader>
        <Brand aria-label="Controlote">
          <BrandLogo src={controloteLogo} alt="" />
          <BrandName>Controlote</BrandName>
        </Brand>
        <DashboardItems>
          {STATUS_ITEMS.map(item => (
            <StatusItem key={item.status} title={item.label}>
              <StatusDot color={item.color} />
              <DesktopMetric>
                <Typography variant="h4" sx={{ color: COLORS.WHITE, fontWeight: 700, lineHeight: 1 }}>
                  {item.isTotal ? total : counts?.[item.status] || 0}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#a9b7cc', lineHeight: 1.15, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  {item.label}
                </Typography>
              </DesktopMetric>
              <MobileMetric>
                <Typography variant="caption" sx={{ color: COLORS.WHITE, fontWeight: 700, lineHeight: 1 }}>
                  {item.compactLabel || item.label.replace('Disponíveis', 'Disp.').replace('Reservados', 'Res.')}{' '}
                  {item.isTotal ? total : counts?.[item.status] || 0}
                </Typography>
              </MobileMetric>
            </StatusItem>
          ))}
        </DashboardItems>
      </DashboardHeader>
    </Dashboard>
  );
};

const EnterprisePublic = () => {
  const { publicPath } = useParams();
  const token = publicPath?.match(/-([a-z0-9]{8})$/i)?.[1];
  const [enterprise, setEnterprise] = useState();
  const [plant, setPlant] = useState();
  const [area, setArea] = useState();
  const [showHelp, setShowHelp] = useState(true);
  const helpTimeoutRef = useRef();
  const dashboardRef = useRef();
  const [plantViewport, setPlantViewport] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      getPublicEnterpriseQuery(token),
      snapshot => {
        setEnterprise(snapshot.empty ? null : snapshot.docs[0].data());
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsubscribe();
  }, [token]);

  useEffect(() => {
    if (!enterprise?.id) return undefined;
    let mounted = true;
    getListImageStorage(enterprise.id, 'plant')
      .then(plants => {
        if (mounted) setPlant(plants[0]?.url);
      })
      .catch(() => {
        if (mounted) setPlant(undefined);
      });
    return () => {
      mounted = false;
    };
  }, [enterprise?.id]);

  useEffect(() => {
    document.title = enterprise?.name ? `${enterprise.name} - Controlote` : 'Empreendimento - Controlote';
  }, [enterprise?.name]);

  useEffect(() => {
    helpTimeoutRef.current = setTimeout(() => setShowHelp(false), 4000);
    return () => clearTimeout(helpTimeoutRef.current);
  }, []);

  useLayoutEffect(() => {
    const updatePlantViewport = () => {
      const viewportWidth = window.visualViewport?.width || window.innerWidth;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      setPlantViewport({
        width: viewportWidth,
        height: Math.max(viewportHeight - (dashboardRef.current?.getBoundingClientRect().height || 0), 0),
      });
    };

    const observer = dashboardRef.current ? new ResizeObserver(updatePlantViewport) : undefined;
    observer?.observe(dashboardRef.current);
    updatePlantViewport();
    const firstFrame = requestAnimationFrame(() => {
      updatePlantViewport();
      requestAnimationFrame(updatePlantViewport);
    });
    window.addEventListener('resize', updatePlantViewport);
    window.visualViewport?.addEventListener('resize', updatePlantViewport);
    window.addEventListener('orientationchange', updatePlantViewport);

    return () => {
      cancelAnimationFrame(firstFrame);
      observer?.disconnect();
      window.removeEventListener('resize', updatePlantViewport);
      window.visualViewport?.removeEventListener('resize', updatePlantViewport);
      window.removeEventListener('orientationchange', updatePlantViewport);
    };
  }, [enterprise?.id, plant]);

  if (!token) return <Enterprise />;

  if (loading) {
    return (
      <Spacing height="100vh" ai="center" jc="center">
        <CircularProgress />
      </Spacing>
    );
  }

  if (!enterprise) {
    return (
      <Spacing height="100vh" ai="center" jc="center" padding="24px">
        <Typography align="center" variant="h5">
          Este empreendimento não está disponível publicamente.
        </Typography>
      </Spacing>
    );
  }

  const getArea = event => setArea(enterprise.forms?.find(item => item?.name === event?.name));
  const openHelp = () => {
    clearTimeout(helpTimeoutRef.current);
    setShowHelp(true);
  };

  return (
    <Spacing display="block" height="100vh" bg={PUBLIC_BACKGROUND} sx="overflow: hidden;">
      <Modal open={showHelp} onClose={() => setShowHelp(false)}>
        <HelpDialog>
          <HelpDialogHeader>
            <Typography variant="h6" sx={{ color: COLORS.WHITE, fontWeight: 700 }}>
              Como navegar no empreendimento
            </Typography>
            <IconButton aria-label="Fechar instruções" onClick={() => setShowHelp(false)} sx={{ color: COLORS.WHITE }}>
              <Close />
            </IconButton>
          </HelpDialogHeader>
          <HelpContent>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: '#a9b7cc', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}
            >
              Touchpad e celular
            </Typography>
            <HelpSteps>
              <HelpStep>
                <TwoFingerMoveIcon />
                <Typography variant="caption" sx={{ color: COLORS.WHITE, lineHeight: 1.2 }}>
                  {window.innerWidth <= 600 ? 'Arraste' : 'Mova com dois dedos'}
                </Typography>
              </HelpStep>
              <HelpStep>
                <Pinch />
                <Typography variant="caption" sx={{ color: COLORS.WHITE, lineHeight: 1.2 }}>
                  Pinça para zoom
                </Typography>
              </HelpStep>
              <HelpStep>
                <TouchApp />
                <Typography variant="caption" sx={{ color: COLORS.WHITE, lineHeight: 1.2 }}>
                  {window.innerWidth <= 600 ? 'Toque no lote' : 'Clique no lote'}
                </Typography>
              </HelpStep>
            </HelpSteps>
            <DesktopHelpSection>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: '#a9b7cc', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}
              >
                Teclado e mouse
              </Typography>
              <HelpSteps>
                <HelpStep>
                  <Mouse />
                  <Typography variant="caption" sx={{ color: COLORS.WHITE, lineHeight: 1.2 }}>
                    Arraste com o botão esquerdo
                  </Typography>
                </HelpStep>
                <HelpStep>
                  <ZoomInMap />
                  <Typography variant="caption" sx={{ color: COLORS.WHITE, lineHeight: 1.2 }}>
                    Roda do mouse para zoom
                  </Typography>
                </HelpStep>
                <HelpStep>
                  <Keyboard />
                  <Typography variant="caption" sx={{ color: COLORS.WHITE, lineHeight: 1.2 }}>
                    Setas e + / − no teclado
                  </Typography>
                </HelpStep>
              </HelpSteps>
            </DesktopHelpSection>
            <ButtonStyled
              variant="contained"
              bg={COLORS.GREEN_LIGHT}
              colorText={COLORS.WHITE}
              onClick={() => setShowHelp(false)}
              margin="16px 0 0 0"
            >
              Entendi
            </ButtonStyled>
          </HelpContent>
        </HelpDialog>
      </Modal>
      <Modal open={Boolean(area)} onClose={() => setArea(undefined)}>
        <LotDialog>
          <LotDialogHeader>
            <Typography variant="h6" sx={{ color: COLORS.WHITE, fontWeight: 700 }}>
              Lote {area?.name}
            </Typography>
            <IconButton
              aria-label="Fechar detalhes do lote"
              onClick={() => setArea(undefined)}
              sx={{ color: COLORS.WHITE }}
            >
              <Close />
            </IconButton>
          </LotDialogHeader>
          <LotDialogBody>
            {area?.description && (
              <LotInfo>
                <LotInfoLabel>Descrição</LotInfoLabel>
                <LotInfoValue>{area.description}</LotInfoValue>
              </LotInfo>
            )}
            <LotInfo>
              <LotInfoLabel>Preço</LotInfoLabel>
              <LotInfoValue>R$ {area?.price || '—'}</LotInfoValue>
            </LotInfo>
            <LotInfo>
              <LotInfoLabel>Status</LotInfoLabel>
              <LotStatus color={area?.status === PLANT_STATUS.AVAILABLE ? COLORS.GREEN_LIGHT : COLORS.RED}>
                {area?.status || '—'}
              </LotStatus>
            </LotInfo>
          </LotDialogBody>
        </LotDialog>
      </Modal>
      <PlantDashboard forms={enterprise.forms} dashboardRef={dashboardRef} />
      <Spacing
        display="block"
        height={plantViewport.height + PLANT_TOP_OFFSET + 'px'}
        margin={`-${PLANT_TOP_OFFSET}px 0 0 0`}
        sx="flex: 1 1 auto; min-height: 0; overflow: hidden; padding: 0;"
      >
        {plant && (
          <ImagePlant
            onClick={getArea}
            enterprise={enterprise}
            plant={plant}
            maxWidth={plantViewport.width}
            maxHeight={Math.max(plantViewport.height - 8, 0)}
            isView
            showBackButton={false}
            showFullscreen
            useFullViewportOnFullscreen={false}
            scrollable={false}
            background={PUBLIC_BACKGROUND}
            enableTouchpadNavigation
            showZoomButtons={false}
            showHelpButton
            onHelp={openHelp}
            viewerControls
          />
        )}
      </Spacing>
    </Spacing>
  );
};

export default EnterprisePublic;
